import React, { ReactNode, Ref } from 'react';
import investoroLogo from 'assets/icons/investoro_pdf_logo.png';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { ReportData } from 'utils/types/AnalyticPanelReport';

import { CostStructureSection } from './CostStructureSection';
import { MarginSection } from './MarginSection';
import { PDF_EXPORT_CLASS } from './pdfExportClass';
import { ReportHeader } from './ReportHeader';
import { StructureSection } from './StructureSection';
import { TimelineCostsChart } from './TimelineCostsChart';

const PDF_PAGE_WIDTH_PX = 1240;
const A4_HEIGHT_TO_WIDTH_RATIO = 297 / 210;
const PDF_PAGE_HEIGHT_PX = Math.round(PDF_PAGE_WIDTH_PX * A4_HEIGHT_TO_WIDTH_RATIO);
const PDF_PAGES_COUNT = 2;

// Renderowane poza ekranem tylko na czas eksportu — html2canvas robi zrzut każdej strony osobno
const PdfWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 100vw;
    width: ${PDF_PAGE_WIDTH_PX}px;
    --pdf-card-border: #DDE3EC;

    h2 {
        font-size: 22px;
    }
`;

const PdfPage = styled.div`
    display: flex;
    flex-direction: column;
    width: ${PDF_PAGE_WIDTH_PX}px;
    height: ${PDF_PAGE_HEIGHT_PX}px;
    padding: 56px 64px;
    background: #ffffff;
    overflow: hidden;
`;

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    margin-bottom: 28px;
    border-bottom: 1px solid var(--color-primary-70);
    font-size: 14px;
    color: var(--color-primary-70);
`;

const PageLogo = styled.img`
    height: 44px;
    width: auto;
`;

const PageFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid var(--color-primary-70);
    font-size: 14px;
    color: var(--color-primary-70);
`;

const PageNumber = styled.span`
    font-weight: 600;
    color: var(--color-primary);
`;

interface PageProps {
    pageRef: Ref<HTMLDivElement>;
    pageNumber: number;
    children: ReactNode;
}

const Page = ({ pageRef, pageNumber, children }: PageProps) => {
    return (
        <PdfPage ref={pageRef}>
            <PageHeader>
                <PageLogo src={investoroLogo} alt="Investoro" />
                <span>Raport inwestycyjny | {dayjs().format('DD.MM.YYYY')}</span>
            </PageHeader>
            {children}
            <PageFooter>
                <span>www.investoro.pl</span>
                <PageNumber>Strona {pageNumber} z {PDF_PAGES_COUNT}</PageNumber>
            </PageFooter>
        </PdfPage>
    );
};

interface AnalyticPanelPdfPagesProps {
    data: ReportData;
    firstPageRef: Ref<HTMLDivElement>;
    secondPageRef: Ref<HTMLDivElement>;
}

const AnalyticPanelPdfPages = ({ data, firstPageRef, secondPageRef }: AnalyticPanelPdfPagesProps) => {
    return (
        // Klasa "light" wymusza jasne kolory motywu — PDF ma zawsze białe tło, także w trybie ciemnym
        <PdfWrapper className={`${PDF_EXPORT_CLASS} light`}>
            <Page pageRef={firstPageRef} pageNumber={1}>
                <ReportHeader data={data} />
                <StructureSection data={data} isAnimationActive={false} />
                <MarginSection data={data} />
            </Page>
            <Page pageRef={secondPageRef} pageNumber={2}>
                <CostStructureSection data={data} isAnimationActive={false} />
                <TimelineCostsChart data={data} isAnimationActive={false} />
            </Page>
        </PdfWrapper>
    );
};

export default AnalyticPanelPdfPages;
